import { local, type WidgetClient } from '@xuwo/contracts';
import {
  createContext,
  createElement,
  type ReactNode,
  useContext,
  useSyncExternalStore,
} from 'react';

const WidgetScope = createContext<{
  client: WidgetClient;
  content: ReactNode;
} | null>(null);

/** The same instance client reaches package, inline and library React trees. */
export function WidgetProvider({
  client,
  content = null,
  children,
}: {
  client: WidgetClient;
  content?: ReactNode;
  children: ReactNode;
}) {
  return createElement(
    WidgetScope.Provider,
    { value: { client, content } },
    children
  );
}

export function useWidget(): WidgetClient {
  const scope = useContext(WidgetScope);
  if (!scope)
    throw new Error('WIDGET_CONTEXT_UNAVAILABLE: render inside a Widget host.');
  return scope.client;
}

/** The host owns the real child blocks; a ReactNode never enters an RPC DTO. */
export function WidgetContent() {
  return useContext(WidgetScope)?.content ?? null;
}

export async function showWidgetMenu(
  xuwo: WidgetClient,
  anchor: HTMLElement,
  items: readonly Readonly<{
    name: string;
    select: () => void;
    disabled?: boolean;
  }>[]
): Promise<void> {
  const { x, y, width, height } = anchor.getBoundingClientRect();
  const selected = await xuwo.ui.menu({
    rect: { x, y, width, height },
    items: items.map((item, index) => ({
      id: String(index),
      name: item.name,
      disabled: item.disabled,
    })),
  });
  if (typeof selected === 'string') items[Number(selected)]?.select();
}

export function useWidgetState<T = unknown>(xuwo: WidgetClient): T {
  return useSyncExternalStore(
    xuwo.state.subscribe,
    xuwo.state.get,
    xuwo.state.get
  ) as T;
}
export const widgetAuthorModule = Object.freeze({
  local,
  useWidgetState,
  useWidget,
  WidgetContent,
  showWidgetMenu,
});
